import {
  EXPLORER_MEMBERSHIP_NAME,
  hasRecognizedSmbfMembership,
  isExplorerMembershipName,
} from "../explorer/isUnpaidExplorerUser";

/** Canonical paid tiers used for badges / connection rules. */
export type MembershipTierName =
  | "Youth Membership"
  | "Early Stage Membership"
  | "Business Membership"
  | "Executive Membership";

export type MembershipKind =
  | "explorer"
  | "youth"
  | "earlyStage"
  | "business"
  | "executive"
  | "unknown";

/** Minimal fields needed to classify a user for display / badges. */
export type MembershipIdentitySource = {
  membershipName?: string | null;
  MembershipName?: string | null;
  explorerAttendeeType?: string | null;
  ExplorerAttendeeType?: string | null;
  hasPaid?: number | null;
  stripeStatus?: string | null;
  userCategory?: string | null;
};

export type MembershipIdentity = {
  kind: MembershipKind;
  /** True when membershipName is empty or "Explorer". */
  isExplorer: boolean;
  /** True when membershipName is a recognized paid tier. */
  isPaidMember: boolean;
  /** Normalized paid tier, or "" for explorer / unknown. */
  tier: MembershipTierName | "";
  /** Raw membershipName from the source. */
  membershipName: string;
  /** Short list label: Explorer, Executive Member, … */
  listLabel: string;
  /** Profile curved badge text: #Explorer, #Executive, … */
  profileBadgeText: string | null;
  /** Profile curved badge stroke color. */
  profileBadgeStroke: string | null;
  /** i18n key under connections.memberBadge.*, or "" */
  badgeI18nKey: string;
};

const PROFILE_THEME: Record<
  Exclude<MembershipKind, "unknown">,
  { text: string; stroke: string }
> = {
  explorer: { text: "#Explorer", stroke: "#0f766e" },
  youth: { text: "#Youth", stroke: "#18c2c6" },
  earlyStage: { text: "#EarlyStage", stroke: "#18c2c6" },
  business: { text: "#Business", stroke: "#1c57b9" },
  executive: { text: "#Executive", stroke: "#544bc1" },
};

const LIST_LABEL: Record<Exclude<MembershipKind, "unknown">, string> = {
  explorer: "Explorer",
  youth: "Youth Member",
  earlyStage: "Early Stage Member",
  business: "Business Member",
  executive: "Executive Member",
};

const BADGE_I18N: Record<Exclude<MembershipKind, "unknown">, string> = {
  explorer: "connections.memberBadge.explorer",
  youth: "connections.memberBadge.youth",
  earlyStage: "connections.memberBadge.earlyStage",
  business: "connections.memberBadge.business",
  executive: "connections.memberBadge.executive",
};

export function normalizeMembershipTier(
  m: string | undefined | null,
): MembershipTierName | "" {
  const s = (m ?? "").trim().toLowerCase();
  if (s.includes("early stage")) return "Early Stage Membership";
  if (s.includes("youth")) return "Youth Membership";
  if (s.includes("business")) return "Business Membership";
  if (s.includes("executive")) return "Executive Membership";
  return "";
}

function kindFromTier(tier: MembershipTierName | ""): MembershipKind {
  switch (tier) {
    case "Youth Membership":
      return "youth";
    case "Early Stage Membership":
      return "earlyStage";
    case "Business Membership":
      return "business";
    case "Executive Membership":
      return "executive";
    default:
      return "unknown";
  }
}

function resolveMembershipName(
  source: MembershipIdentitySource | null | undefined,
): string {
  if (!source) return "";
  return String(source.membershipName ?? source.MembershipName ?? "").trim();
}

function identityFromKind(
  kind: MembershipKind,
  membershipName: string,
  flags: { isExplorer: boolean; isPaidMember: boolean },
): MembershipIdentity {
  const tier =
    kind === "explorer" || kind === "unknown"
      ? ("" as const)
      : (normalizeMembershipTier(
          kind === "youth"
            ? "Youth Membership"
            : kind === "earlyStage"
              ? "Early Stage Membership"
              : kind === "business"
                ? "Business Membership"
                : "Executive Membership",
        ) as MembershipTierName | "");

  if (kind === "unknown") {
    return {
      kind,
      isExplorer: flags.isExplorer,
      isPaidMember: flags.isPaidMember,
      tier: "",
      membershipName,
      listLabel: membershipName || "",
      profileBadgeText: null,
      profileBadgeStroke: null,
      badgeI18nKey: "",
    };
  }

  const theme = PROFILE_THEME[kind];
  return {
    kind,
    isExplorer: flags.isExplorer,
    isPaidMember: flags.isPaidMember,
    tier,
    membershipName,
    listLabel: LIST_LABEL[kind],
    profileBadgeText: theme.text,
    profileBadgeStroke: theme.stroke,
    badgeI18nKey: BADGE_I18N[kind],
  };
}

/**
 * Single source of truth for Explorer vs paid-tier display / gates.
 *
 * Name-only (no payment checks):
 * - empty or "Explorer" → Explorer
 * - recognized paid tier → that tier
 * - anything else → unknown label from raw name
 */
export function resolveMembershipIdentity(
  source: MembershipIdentitySource | null | undefined,
): MembershipIdentity {
  if (!source) {
    return identityFromKind("explorer", "", {
      isExplorer: true,
      isPaidMember: false,
    });
  }

  const membershipName = resolveMembershipName(source);

  if (isExplorerMembershipName(membershipName)) {
    return identityFromKind("explorer", membershipName || EXPLORER_MEMBERSHIP_NAME, {
      isExplorer: true,
      isPaidMember: false,
    });
  }

  const tier = normalizeMembershipTier(membershipName);
  if (tier || hasRecognizedSmbfMembership(membershipName)) {
    return identityFromKind(kindFromTier(tier), membershipName, {
      isExplorer: false,
      isPaidMember: true,
    });
  }

  return identityFromKind("unknown", membershipName, {
    isExplorer: false,
    isPaidMember: false,
  });
}

/** Convenience: list / card membership label. */
export function getMembershipListLabel(
  source: MembershipIdentitySource | null | undefined,
): string {
  return resolveMembershipIdentity(source).listLabel;
}

/** Convenience: connections.memberBadge.* i18n key. */
export function getMembershipBadgeI18nKey(
  source: MembershipIdentitySource | string | null | undefined,
): string {
  if (typeof source === "string") {
    if (isExplorerMembershipName(source)) return BADGE_I18N.explorer;
    const kind = kindFromTier(normalizeMembershipTier(source));
    return kind === "unknown" ? "" : BADGE_I18N[kind];
  }
  return resolveMembershipIdentity(source).badgeI18nKey;
}

/** Convenience: profile curved badge theme. */
export function getMembershipProfileBadgeTheme(
  source: MembershipIdentitySource | null | undefined,
): { stroke: string; text: string } | null {
  const id = resolveMembershipIdentity(source);
  if (!id.profileBadgeText || !id.profileBadgeStroke) return null;
  return { stroke: id.profileBadgeStroke, text: id.profileBadgeText };
}
